/**
 * <sofa-configurator>
 * Renders a modular-product configurator from a JSON graph (see
 * snippets/configurator-graph.liquid). Live-updates image + price, resolves the
 * selected combination into module variants, and adds them to cart as a bundle.
 *
 * Data contract (mock === real metaobject output):
 *   graph.options[].sub_options[].values[]
 *   value.variant_option_value          -> matches module variant option value
 *   value.module_slots[] {role, quantity, module}
 *   module {handle, title, option_names[], variants[{id, price, options{}, available}]}
 */
if (!customElements.get('sofa-configurator')) {
  // Chrome glyphs exported from the design (Figma node 45:12832). Inlined rather
  // than linked because the cards are built in JS and the exported asset URLs
  // expire; geometry is the exported path data, recoloured via currentColor.
  const CFG_CHEVRON = `<svg width='27' height='27' viewBox='0 0 27 27' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true' focusable='false'><path d='M21.375 16.875L13.5 9L5.625 16.875' stroke='currentColor' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'/></svg>`;
  const CFG_INFO = `<svg width='13.8667' height='13.8667' viewBox='0 0 13.8667 13.8667' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true' focusable='false'><path d='M6.93 3.41467V7.63733M6.93333 0.6C5.25363 0.6 3.64272 1.26726 2.45499 2.45499C1.26726 3.64272 0.6 5.25363 0.6 6.93333C0.6 8.61304 1.26726 10.2239 2.45499 11.4117C3.64272 12.5994 5.25363 13.2667 6.93333 13.2667C8.61304 13.2667 10.2239 12.5994 11.4117 11.4117C12.5994 10.2239 13.2667 8.61304 13.2667 6.93333C13.2667 5.25363 12.5994 3.64272 11.4117 2.45499C10.2239 1.26726 8.61304 0.6 6.93333 0.6Z' stroke='currentColor' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/><path d='M6.96346 9.97109H6.95596' stroke='currentColor' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/></svg>`;

  customElements.define('sofa-configurator', class SofaConfigurator extends HTMLElement {
    constructor() {
      super();
      try {
        this.graph = JSON.parse(this.querySelector('[data-configurator-data]').textContent);
      } catch (e) {
        console.error('[sofa-configurator] invalid graph JSON', e);
        return;
      }
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      this.els = {
        options: this.querySelector('[data-configurator-options]'),
        image: this.querySelector('[data-configurator-image]'),
        price: this.querySelector('[data-configurator-price]'),
        addPrice: this.querySelector('[data-configurator-add-price]'),
        title: this.querySelector('[data-configurator-product-title]'),
        summary: this.querySelector('[data-configurator-selection-summary]'),
        modules: this.querySelector('[data-configurator-modules]'),
        modulesCount: this.querySelector('[data-configurator-modules-count]'),
        add: this.querySelector('[data-configurator-add]'),
        addLabel: this.querySelector('[data-configurator-add-label]'),
        stockStatus: this.querySelector('[data-configurator-stock-status]'),
        // the size card lives in its own block: bind to the one in THIS product section only,
        // so another configurator on the page (quick view, etc.) can't overwrite it
        sizeWrap: (this.closest('section') || document).querySelector('[data-configurator-size-wrap]'),
        size: null,
      };
      this.els.size = this.els.sizeWrap ? this.els.sizeWrap.querySelector('[data-configurator-size]') : null;
      this.sizeUnit = 'imperial';
      try { this.sizeUnit = localStorage.getItem('mb-size-unit') === 'metric' ? 'metric' : 'imperial'; } catch (e) { /* storage blocked */ }
      if (this.els.sizeWrap) {
        this.els.sizeWrap.querySelectorAll('[data-size-unit]').forEach((b) => b.addEventListener('click', () => {
          this.sizeUnit = b.dataset.sizeUnit === 'metric' ? 'metric' : 'imperial';
          try { localStorage.setItem('mb-size-unit', this.sizeUnit); } catch (e) { /* storage blocked */ }
          this.renderSize(this.lastLines || []);
        }));
      }
      this.addLabelText = this.els.addLabel ? this.els.addLabel.textContent : '';
      this.unavailableLabelText = this.dataset.unavailableLabel || 'Not available';
      this.soldOutLabelText = this.dataset.soldOutLabel || 'Sold out';
      this.soldOutLayouts = new Set();
      this.soldOutValues = {};
      this.preorderDeliveryText = this.dataset.preorderDelivery || 'Pre-Order for delivery around __DATE__';

      // Price display target: the theme's price block (if data-price-target set),
      // else the inline element. We update the regular price amount node(s), and,
      // when the current combination includes a discounted module (see
      // snippets/configurator-module.liquid's `discounted_price`), the sale/compare
      // nodes and the containers' .price--on-sale class instead - no percent-off
      // badge node exists for the configurator price (see hide_percent_off in
      // snippets/price.liquid), so there's nothing to keep in sync there.
      this.priceNodes = [];
      this.priceSaleNodes = [];
      this.priceCompareNodes = [];
      this.priceContainers = [];
      const priceTarget = this.dataset.priceTarget && document.getElementById(this.dataset.priceTarget);
      if (priceTarget) {
        // The configurator total is the only price. Default to the regular slot
        // and drop the sale state: the compare-at node also carries
        // .price-item--regular, so writing to every match put the total in the
        // struck-through slot and left a stale sale price beside it. update()
        // re-adds price--on-sale per render when the resolved total is discounted.
        priceTarget.querySelectorAll('.price--on-sale').forEach((el) => el.classList.remove('price--on-sale'));
        this.priceNodes = Array.from(priceTarget.querySelectorAll('.price__regular .price-item--regular'));
        this.priceSaleNodes = Array.from(priceTarget.querySelectorAll('.price__sale .price-item--sale'));
        this.priceCompareNodes = Array.from(priceTarget.querySelectorAll('.price__sale .price__compare .price-item--regular'));
        this.priceContainers = Array.from(priceTarget.querySelectorAll('.price'));
      }
      if (!this.priceNodes.length && this.els.price) this.priceNodes = [this.els.price];

      // Theme product-gallery main image(s) — swapped when a config_image matches.
      // Main gallery is .product__media-list (full-width); also the zoom modal.
      this.galleryTargets = [];
      ['.product__media-list', '.product__media-sublist', '.product-media-modal__content'].forEach((sel) => {
        const img = document.querySelector(`${sel} .product__media-item img`) || document.querySelector(`${sel} .product__media-subitem img`) || document.querySelector(`${sel} img`);
        if (img) this.galleryTargets.push({ img: img, src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), swiperEl: img.closest('.swiper') });
      });

      const pageLang = document.documentElement.lang || '';
      this.money = new Intl.NumberFormat(/^fr/i.test(pageLang) ? 'fr-CA' : undefined, {
        style: 'currency',
        currency: this.graph.currency || 'USD',
        currencyDisplay: 'narrowSymbol',
      });

      this.buildIndex();
      this.initSelection();
      this.renderOptions();
      this.bindEvents();
      this.update();
    }

    /* ---- indexing --------------------------------------------------------- */
    // Flatten sub-options into an addressable list; key = "optionIdx-subIdx".
    buildIndex() {
      this.subs = [];
      (this.graph.options || []).forEach((opt, oi) => {
        (opt.sub_options || []).forEach((sub, si) => {
          this.subs.push({ key: `${oi}-${si}`, oi, si, sub, option: opt });
        });
      });
      this.selected = {}; // key -> value index
    }

    initSelection() {
      this.subs.forEach(({ key, sub }) => {
        this.selected[key] = 0; // first value (add-ons list "None" first)
      });
      this.selectMinimumCombination();
    }

    // Start on the cheapest combination that can actually be bought: the layout
    // and every variant axis (size, fabric/colour) are searched, add-ons stay on
    // "None" since they can only add cost. Falls back to the first values when
    // nothing resolves or the search space is unreasonably large.
    selectMinimumCombination() {
      const dims = this.subs.filter((e) => e.sub.variant_option_name || e.sub.name === 'Layout');
      if (!dims.length) return;
      const space = dims.reduce((n, e) => n * Math.max(1, e.sub.values.length), 1);
      if (space > 50000) return;

      let best = null;
      const pick = dims.map(() => 0);
      const walk = (d) => {
        if (d === dims.length) {
          const { total, resolvable } = this.resolveCart();
          if (resolvable && (best === null || total < best.total)) best = { total, pick: pick.slice() };
          return;
        }
        dims[d].sub.values.forEach((_, i) => {
          pick[d] = i;
          this.selected[dims[d].key] = i;
          walk(d + 1);
        });
      };
      walk(0);
      dims.forEach((e, d) => { this.selected[e.key] = best ? best.pick[d] : 0; });
    }

    // Values that cannot be bought given the OTHER current choices: picking one
    // would leave a piece with no available variant. They stay visible but are
    // disabled. The current value and "None" are never disabled, so there is
    // always a way out of an unavailable combination.
    // Names of the add-ons (Chaise, Bumper, Armrest) the CURRENT layout requires.
    requiredAddons() {
      const layoutEntry = this.subs.find((e) => e.sub.name === 'Layout');
      return new Set(((layoutEntry && this.valueOf(layoutEntry)) || {}).require || []);
    }

    // Is the current pick sold out: the layout itself, or a required add-on value in this colour.
    selectionSoldOut() {
      return this.subs.some((e) => {
        const set = e.sub.name === 'Layout' ? this.soldOutLayouts : this.soldOutValues[e.key];
        return !!set && set.has(this.selected[e.key]);
      });
    }

    computeUnavailable() {
      const current = Object.assign({}, this.selected);
      this.unavailable = {};
      this.soldOutValues = {};
      this.subs.forEach((entry) => {
        const blocked = new Set();
        // Real variant axes (Size/Color) and Layout are primary choices the shopper
        // must always be able to switch between — e.g. picking a layout only sold
        // in colour 3, then trying to switch back to colour 2, must not lock colour
        // 2 out. The fix belongs to whichever pick is actually incompatible (shown
        // via the overall "Not available" / disabled Add button, or auto-resolved by
        // resetInvalidAddons/autoFixLayoutForVariantChange below) — not a block here.
        // Only add-on subs (Chaise, Bumper, Ottoman, ...) get disabled this way.
        const isPrimary = !!entry.sub.variant_option_name || entry.sub.name === 'Layout';
        if (!isPrimary && this.requiredAddons().has(entry.sub.name)) {
          // an add-on the layout REQUIRES: a value that is out of stock in this colour stays
          // selectable but is flagged sold out (never silently reset to None, which is not allowed)
          const chosen = this.chosenVariantOptions();
          const sold = new Set();
          entry.sub.values.forEach((val, i) => {
            if (val.label !== 'None' && !this.valueResolves(val, chosen)) sold.add(i);
          });
          this.soldOutValues[entry.key] = sold;
        } else if (!isPrimary) {
          entry.sub.values.forEach((val, i) => {
            if (i === current[entry.key] || val.label === 'None') return;
            this.selected[entry.key] = i;
            if (!this.resolveCart().resolvable) blocked.add(i);
          });
          this.selected[entry.key] = current[entry.key];
        }
        // A layout that REQUIRES an add-on (chaise/bumper/armrest) cannot be bought when none of
        // that add-on's allowed choices is in stock in the current colour. It stays selectable
        // (nothing is blocked) but is flagged sold out: faded tile + "Sold out" button.
        if (entry.sub.name === 'Layout') {
          this.soldOutLayouts = new Set();
          entry.sub.values.forEach((val, i) => {
            if (!(val.require && val.require.length)) return;
            if (!this.layoutBuyable(entry, i)) this.soldOutLayouts.add(i);
          });
        }
        this.unavailable[entry.key] = blocked;
      });
    }

    // Can this layout be bought with the current colour? Try every combination of its REQUIRED
    // add-ons (None excluded, layout rules applied); true as soon as one resolves. Restores the
    // current selection and constraints afterwards.
    layoutBuyable(layoutEntry, layoutIdx) {
      const saved = Object.assign({}, this.selected);
      const savedLast = this.lastChanged;
      const savedConstraints = this.constraints;
      this.lastChanged = null;
      this.selected[layoutEntry.key] = layoutIdx;
      this.enforceConstraints();
      const required = new Set(layoutEntry.sub.values[layoutIdx].require || []);
      const subs = this.subs.filter((e) => e !== layoutEntry && required.has(e.sub.name));
      // optional add-ons (not required, not a variant axis) start at None so an unrelated
      // pick cannot change the result; variant options (colour) keep the shopper's choice
      this.subs.forEach((e) => {
        if (e === layoutEntry || required.has(e.sub.name) || e.sub.variant_option_name) return;
        const none = e.sub.values.findIndex((v) => v.label === 'None');
        if (none >= 0) this.selected[e.key] = none;
      });
      // a combination is valid when every chosen value's rules accept the other add-ons' picks
      const rulesOk = () => this.subs.every((e) => {
        if (e === layoutEntry) return true;
        const label = (e.sub.values[this.selected[e.key]] || {}).label;
        return this.subs.every((o) => {
          if (o === e || o === layoutEntry) return true;
          const rule = ((o.sub.values[this.selected[o.key]] || {}).rules || {})[e.sub.name];
          return !Array.isArray(rule) || rule.includes(label) || (label === 'None' && !required.has(e.sub.name));
        });
      });
      const choices = subs.map((e) => {
        const allowed = this.constraints[e.key];
        return e.sub.values.map((v, i) => i).filter((i) => {
          const label = e.sub.values[i].label;
          return label !== 'None' && (!allowed || allowed.has(label));
        });
      });
      let ok = false;
      const walk = (n) => {
        if (ok) return;
        if (n === subs.length) { ok = rulesOk() && this.resolveCart().resolvable; return; }
        choices[n].forEach((i) => { this.selected[subs[n].key] = i; walk(n + 1); });
      };
      walk(0);
      this.selected = saved;
      this.lastChanged = savedLast;
      this.constraints = savedConstraints;
      return ok;
    }

    // Whether a value's own module_slots all resolve to an available variant under
    // the CURRENTLY chosen variant options (colour/size). Shared by the two
    // reconciliation passes below.
    valueResolves(val, chosen) {
      return !!val && Array.isArray(val.module_slots) && val.module_slots.length > 0 &&
        val.module_slots.every((slot) => {
          const v = this.resolveVariant(slot.module, chosen);
          return v && v.available;
        });
    }

    // An add-on (Chaise/Bumper/Ottoman/Armrest) that was fine in one colour can
    // become unbuyable purely because another axis changed (e.g. this colour has
    // no variant for that piece). computeUnavailable() never flags the CURRENT
    // pick — only the other, unselected values — so left alone this would keep
    // showing the broken add-on as selected while it's actually not purchasable.
    // Reset it to "None" the same way an invalid rule-based pick resets above.
    // Add-ons can never be picked while already invalid (they're not "primary", so
    // computeUnavailable disables them at click time) — this only ever fires as a
    // side effect of a LATER change elsewhere, never of the add-on's own click.
    resetInvalidAddons() {
      const chosen = this.chosenVariantOptions();
      this.subs.forEach((entry) => {
        const isPrimary = !!entry.sub.variant_option_name || entry.sub.name === 'Layout';
        if (isPrimary) return;
        const val = entry.sub.values[this.selected[entry.key]];
        if (!val || val.label === 'None' || !Array.isArray(val.module_slots) || !val.module_slots.length) return;
        // a required add-on keeps its (sold out) pick: None is not allowed for it
        if (this.requiredAddons().has(entry.sub.name)) return;
        if (!this.valueResolves(val, chosen)) {
          const noneIdx = entry.sub.values.findIndex((v) => v.label === 'None');
          this.selected[entry.key] = noneIdx < 0 ? 0 : noneIdx;
        }
      });
    }

    // Deliberately NOT auto-switching Layout to a different one when a Colour/Size
    // change breaks it: the shopper picked that shape on purpose, and silently
    // swapping it for an unrelated "first available" layout is a worse surprise
    // than just showing "Not available". resetInvalidAddons() already strips any
    // Chaise/Bumper/Ottoman that the new colour can't support; if the LAYOUT's own
    // base pieces still don't resolve after that, the overall unresolved state
    // (price falls back to "From $X", Add button disabled) is the correct signal —
    // the shopper stays on their chosen layout and picks a different colour or
    // layout themselves, same as Colour/Size were already never blocked from a click.

    valueOf(entry) {
      return entry.sub.values[this.selected[entry.key]];
    }

    /* ---- resolution ------------------------------------------------------- */
    // Map of variant_option_name -> chosen value string, from current selection.
    chosenVariantOptions() {
      const map = {};
      this.subs.forEach((entry) => {
        const name = entry.sub.variant_option_name;
        if (!name) return;
        const val = this.valueOf(entry);
        if (val && val.variant_option_value != null) map[name] = val.variant_option_value;
      });
      return map;
    }

    // Combine module_slots across every selected value.
    // Slots WITHOUT a role are additive (the default, unchanged behaviour). Slots
    // WITH a role are keyed last-write-wins, so a later option (e.g. Chaise) can
    // REPLACE an earlier one (e.g. the layout's end chair). Options are processed
    // in graph-array order, so a replacing option must sit after the one it overrides.
    computeBOM() {
      const layoutEntry = this.subs.find((e) => e.sub.name === 'Layout');
      const layoutVal = layoutEntry ? (this.valueOf(layoutEntry) || {}) : {};
      const compositions = layoutVal.compositions || {};
      const composed = new Set();
      Object.keys(compositions).forEach((subName) => {
        const entry = this.subs.find((e) => e.sub.name === subName);
        const label = entry ? (this.valueOf(entry) || {}).label : '';
        const slots = compositions[subName] && compositions[subName][label];
        if (Array.isArray(slots)) composed.add(subName);
      });
      const additive = [];
      const roled = new Map();
      const pushSlot = (slot) => {
        if (!slot) return;
        if (slot.role) roled.set(slot.role, slot);
        else additive.push(slot);
      };
      this.subs.forEach((entry) => {
        const val = this.valueOf(entry);
        if (!val || !Array.isArray(val.module_slots)) return;
        val.module_slots.forEach(pushSlot);
      });
      return [...additive, ...roled.values()];
    }

    // Find the module variant matching the chosen options (only on axes the
    // module actually has). Returns variant or null.
    resolveVariant(module, chosen) {
      // match only on axes this module has AND the shopper chose; a module with
      // no matching axes (e.g. a single-variant add-on) resolves to its one variant.
      const axes = (module.option_names || []).filter((a) => Object.prototype.hasOwnProperty.call(chosen, a));
      // Several variants can match (the shopper only picks colour, never size/configuration):
      // use the first one that can really be bought (in stock AND not "limited"/"view in
      // store", see variant-stock-status.liquid), then any in stock, else the first one
      // (reported as unavailable).
      const matches = (module.variants || []).filter((v) =>
        axes.every((axis) => v.options[axis] === chosen[axis])
      );
      const buyable = (v) => v.available && v.stock_status !== 'limited' && v.stock_status !== 'viewstore';
      return matches.find(buyable) || matches.find((v) => v.available) || matches[0] || null;
    }

    // Returns {lines:[{variant,slot}], total, discountedTotal, resolvable}.
    // discountedTotal applies each line's own module discount (variant.discounted_price,
    // set by snippets/configurator-module.liquid when the module's collection carries an
    // eligible custom.discount_percentage / discounted_skus / discount_expiry); it equals
    // total when nothing in the current combination is discounted.
    // Also returns {limited, preorder, latestArrival} from each piece's stock_status
    // (see snippets/variant-stock-status.liquid): the bundle is "limited" (not
    // resolvable, add to cart locked) when ANY piece is limited, and "preorder" when ANY piece is pre-order,
    // with the LATEST arrival date among the pre-order pieces.
    resolveCart() {
      const chosen = this.chosenVariantOptions();
      const bom = this.computeBOM();
      const lines = [];
      let total = 0;
      let discountedTotal = 0;
      let resolvable = bom.length > 0;
      let limited = false;
      let preorder = false;
      let latestArrival = '';
      const sizeLines = [];
      // price of the pick even when a piece is sold out (used to show a sold-out layout's price)
      let pricedTotal = 0;
      let pricedDiscountedTotal = 0;
      let priceable = bom.length > 0;
      const todayStr = this.todayIso();
      bom.forEach((slot) => {
        const variant = this.resolveVariant(slot.module, chosen);
        if (!variant) { resolvable = false; priceable = false; return; }
        sizeLines.push({ variant, slot });
        const pq = slot.quantity || 1;
        pricedTotal += variant.price * pq;
        pricedDiscountedTotal += (variant.discounted_price != null ? variant.discounted_price : variant.price) * pq;
        if (!variant.available) { resolvable = false; return; }
        if (variant.stock_status === 'limited' || variant.stock_status === 'viewstore') {
          // not purchasable: same as a missing/unavailable piece (fallback "From" price),
          // but the line stays so the breakdown can tag it "Not available"
          limited = true;
          resolvable = false;
        }
        if (variant.stock_status === 'preorder') {
          preorder = true;
          if (variant.arrival && variant.arrival >= todayStr && variant.arrival > latestArrival) latestArrival = variant.arrival;
        }
        const qty = slot.quantity || 1;
        const unitPrice = variant.discounted_price != null ? variant.discounted_price : variant.price;
        total += variant.price * qty;
        discountedTotal += unitPrice * qty;
        lines.push({ variant, slot });
      });
      return { lines, sizeLines, total, discountedTotal, resolvable, limited, preorder, latestArrival, pricedTotal, pricedDiscountedTotal, priceable };
    }

    /* ---- rendering: options ---------------------------------------------- */
    renderOptions() {
      // Sort the ORDER, not the array: every data-* key downstream (buildIndex,
      // paintSelections) addresses cards by their index in graph.options, so the
      // index handed to renderCard has to stay the original one.
      const opts = this.graph.options || [];
      const order = opts.map((_, i) => i).sort((a, b) => (opts[a].position || 0) - (opts[b].position || 0));
      this.els.options.innerHTML = order.map((oi) => this.renderCard(opts[oi], oi)).join('');
      // every card starts unfolded; collapsing is entirely the shopper's choice
      this.els.options.querySelectorAll('.config-card').forEach((c) => {
        c.classList.add('is-open');
        const t = c.querySelector('[data-card-toggle]');
        if (t) t.setAttribute('aria-expanded', 'true');
      });
    }

    // A card's "View …" link points at whichever URL the merchant configured for
    // that kind of documentation (set as data-* on the host element).
    docHref(kind) {
      const map = { care: this.dataset.careUrl, docs: this.dataset.docsUrl, dimensions: this.dataset.dimensionsUrl };
      return map[kind] || '';
    }

    renderCard(opt, oi) {
      // Every choice group carries its own quiet label ("Color", "Ottoman", …),
      // including on single-sub cards.
      const body = (opt.sub_options || [])
        .map((sub, si) => this.renderSub(sub, `${oi}-${si}`, false))
        .join('');

      const href = this.docHref(opt.doc_kind);
      const doc = opt.doc_label && href
        ? `<a class='config-card__doc' href='${this.esc(href)}'>${this.esc(opt.doc_label)}</a>`
        : '';

      // Optional trailing CTA ("Request free fabric swatches") and closing note.
      const ctaHref = this.dataset.swatchUrl;
      const cta = opt.cta_label && ctaHref
        ? `<a class='config-card__cta' href='${this.esc(ctaHref)}'>${this.esc(opt.cta_label)}</a>`
        : '';
      const note = opt.note ? `<p class='config-card__note'>${this.esc(opt.note)}</p>` : '';

      const title = opt.tagline ? `${opt.name} - ${opt.tagline}` : opt.name;

      // Header reads: [thumb of current selection] [step no.] name — tagline
      //               [selections]                        [docs link]  [chevron]
      return `
        <div class='config-card' data-card='${oi}'>
          <div class='config-card__header'>
            <button type='button' class='config-card__toggle' data-card-toggle='${oi}' aria-expanded='false'>
              <span class='visually-hidden'>${this.esc(title)}</span>
            </button>
            <span class='config-card__thumb' data-card-thumb='${oi}' aria-hidden='true'></span>
            <span class='config-card__heading'>
              <span class='config-card__title'>
                <span class='config-card__step' aria-hidden='true'>${oi + 1}</span>
                <span class='config-card__name'>${this.esc(title)}</span>
              </span>
              <span class='config-card__meta'>
                <span class='config-card__value' data-card-summary='${oi}'></span>
                ${doc}
              </span>
            </span>
            <span class='config-card__chevron' aria-hidden='true'>${CFG_CHEVRON}</span>
          </div>
          <div class='config-card__body'><div class='config-card__body-inner'>${body}${cta}${note}</div></div>
        </div>`;
    }

    renderSub(sub, key, hideLabel) {
      const type = sub.display_type || 'button';
      const info = sub.info ? `<span class='config-sub__info'>${CFG_INFO}</span>` : '';
      const label = hideLabel ? '' : `
          <div class='config-sub__label'>
            ${this.esc(sub.name)}${info}
            <span class='config-sub__current' data-sub-current='${key}'></span>
          </div>`;
      return `
        <div class='config-sub' data-sub='${key}'>
          ${label}
          ${this['renderSub_' + type] ? this['renderSub_' + type](sub, key) : this.renderSub_button(sub, key)}
        </div>`;
    }

    renderSub_button(sub, key) {
      const btns = sub.values.map((v, i) =>
        `<button type='button' class='config-pill' data-sub-key='${key}' data-value='${i}'>${this.esc(v.label)}${v.info ? `<span class='config-choice__info'>${this.esc(v.info)}</span>` : ''}</button>`
      ).join('');
      return `<div class='config-pills'>${btns}</div>`;
    }

    renderSub_dropdown(sub, key) {
      const opts = sub.values.map((v, i) =>
        `<option value='${i}'>${this.esc(v.label)}</option>`
      ).join('');
      return `<select class='config-select' data-sub-key='${key}'>${opts}</select>`;
    }

    /*
     * The colour axis renders two ways in one block. Values with no group_label
     * are the plain colour row ("Color") and draw as flat 64px chips. Values that
     * carry a group_label are fabrics — they draw as photo tiles under a heading
     * for their material ("Chenille", "Aquaforte", "Performance Fabric").
     */
    renderSub_swatch(sub, key) {
      const groups = {};
      sub.values.forEach((v, i) => {
        const g = v.group_label || '';
        (groups[g] = groups[g] || []).push({ v, i });
      });
      return Object.keys(groups).map((g) => {
        if (!g) return `<div class='config-swatches'>${groups[g].map(({ v, i }) => this.renderChip(v, i, key)).join('')}</div>`;
        return `<div class='config-sub__label'>${this.esc(g)}</div>
                <div class='config-fabrics'>${groups[g].map(({ v, i }) => this.renderFabric(v, i, key)).join('')}</div>`;
      }).join('');
    }

    // Flat colour chip — the swatch hex is the point, so it wins over any photo.
    renderChip(v, i, key) {
      const bg = v.hex || this.cssColorFromName(v.label);
      const style = bg
        ? `background:${bg};`
        : (v.thumb ? `background-image:url(${v.thumb});` : 'background:#ccc;');
      return `<button type='button' class='config-swatch' data-sub-key='${key}' data-value='${i}'
         title='${this.esc(v.label)}${v.info ? ' — ' + this.esc(v.info) : ''}' aria-label='${this.esc(v.label)}'>
         <span style='${style}'></span>
       </button>`;
    }

    // Fabric tile — photo fills the tile, name sits over the scrim at the bottom.
    renderFabric(v, i, key) {
      const style = v.thumb ? ` style='background-image:url(${v.thumb})'` : '';
      return `<button type='button' class='config-fabric' data-sub-key='${key}' data-value='${i}'
         title='${this.esc(v.label)}'${style}><span>${this.esc(v.label)}</span></button>`;
    }

    renderSub_layout(sub, key) {
      const tiles = sub.values.map((v, i) => {
        // With a thumbnail, show the image only (label kept for a11y via title);
        // without one, fall back to the text label (+ optional info).
        const inner = v.thumb
          ? `<img class='config-tile__img' src='${v.thumb}' alt='${this.esc(v.label)}'>`
          : `<span>${this.esc(v.label)}</span>${v.info ? `<span class='config-choice__info'>${this.esc(v.info)}</span>` : ''}`;
        // The "None" glyph is drawn smaller than the layout icons.
        const none = /--none$/.test(v.id || '') ? ' config-tile--none' : '';
        return `<button type='button' class='config-tile${none}' data-sub-key='${key}' data-value='${i}'
             title='${this.esc(v.label)}' aria-label='${this.esc(v.label)}'>${inner}</button>`;
      }).join('');
      return `<div class='config-tiles'>${tiles}</div>`;
    }

    /* ---- events ----------------------------------------------------------- */
    bindEvents() {
      this.els.options.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-card-toggle]');
        if (toggle) { this.toggleCard(toggle.dataset.cardToggle); return; }

        const choice = e.target.closest('[data-value]');
        if (choice) {
          this.select(choice.dataset.subKey, parseInt(choice.dataset.value, 10));
        }
      });

      this.els.options.addEventListener('change', (e) => {
        const sel = e.target.closest('select[data-sub-key]');
        if (sel) this.select(sel.dataset.subKey, parseInt(sel.value, 10));
      });

      this.els.add.addEventListener('click', () => this.addToCart());

      // A tile whose configured icon 404s (missing/broken merchant upload for this
      // layout's add-on) must not sit there as a dead broken-image box — but the
      // option itself is still real and still purchasable, so it must NOT be
      // disabled over a cosmetic asset gap. Fall back to the plain text label
      // instead, same as a tile that never had a thumbnail to begin with
      // (renderSub_layout's own fallback). img "error" doesn't bubble, so this
      // listens in the capture phase. If a later layout change points this same
      // tile at a different (working) icon, updateAddonPresentation() finds no
      // <img> left, re-inserts one, and it can succeed normally.
      this.els.options.addEventListener('error', (e) => {
        if (!e.target.matches || !e.target.matches('.config-tile__img')) return;
        const btn = e.target.closest('.config-tile');
        if (!btn) return;
        const entry = this.subs.find((s) => s.key === btn.dataset.subKey);
        const val = entry && entry.sub.values[parseInt(btn.dataset.value, 10)];
        e.target.remove();
        if (val && !btn.querySelector('span')) {
          btn.insertAdjacentHTML('beforeend', `<span>${this.esc(val.label)}</span>`);
        }
      }, true);
    }

    // Cards fold independently — any number may be open, including none.
    toggleCard(oi) {
      const card = this.els.options.querySelector(`.config-card[data-card='${oi}']`);
      if (!card) return;
      const open = card.classList.toggle('is-open');
      const toggle = card.querySelector('[data-card-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    // A value is disabled when the layout rules forbid it or when it cannot be
    // bought with the current choices. Shared by the paint pass and select().
    isValueDisabled(key, valueIdx) {
      const entry = this.subs.find((s) => s.key === key);
      const val = entry && entry.sub.values[valueIdx];
      const allowed = this.constraints && this.constraints[key];
      const blocked = !!allowed && !allowed.has((val || {}).label);
      const unavailable = !!(this.unavailable && this.unavailable[key] && this.unavailable[key].has(valueIdx));
      return blocked || unavailable;
    }

    select(key, valueIdx) {
      // ignore choices that are disabled; repaint so a native <select> snaps back
      // to the value that is actually selected
      if (this.isValueDisabled(key, valueIdx)) { this.paintSelections(); return; }
      if (this.selected[key] === valueIdx) return;
      this.selected[key] = valueIdx;
      this.lastChanged = key;
      this.update();
    }

    /* ---- constraints ------------------------------------------------------ */
    // Two kinds of restriction:
    //  - the LAYOUT's rules (e.g. {"Chaise": ["Left","Right"]}) disable tiles. None is always
    //    allowed unless the layout REQUIRES the add-on (layout.require).
    //  - a chosen add-on value's rules (e.g. Chaise Right -> {"Bumper": ["Left"]}) never disable a
    //    tile: they SWITCH the other add-on. The option the shopper just changed keeps its pick and the
    //    others adapt to it, in order (Layout, the changed option, then the rest in graph order), so
    //    Chaise Right while Bumper is Right moves Bumper to Left (or None when None is allowed).
    enforceConstraints() {
      const layoutEntry = this.subs.find((e) => e.sub.name === 'Layout');
      const layoutVal = layoutEntry ? (this.valueOf(layoutEntry) || {}) : {};
      const layoutRules = layoutVal.rules || {};
      const required = new Set(layoutVal.require || []);
      this.constraints = {};
      this.subs.forEach((e) => {
        const lr = layoutRules[e.sub.name];
        let allowed = null;
        if (Array.isArray(lr)) { allowed = new Set(lr); allowed.add('None'); }
        if (required.has(e.sub.name)) {
          if (!allowed) allowed = new Set(e.sub.values.map((v) => v.label));
          allowed.delete('None');
        }
        this.constraints[e.key] = allowed;
      });
      const changed = this.subs.find((e) => e.key === this.lastChanged);
      const order = [];
      if (layoutEntry) order.push(layoutEntry);
      if (changed && !order.includes(changed)) order.push(changed);
      this.subs.forEach((e) => { if (!order.includes(e)) order.push(e); });
      const resolved = [];
      order.forEach((e) => {
        const keepNone = !required.has(e.sub.name);
        let allowed = this.constraints[e.key] ? new Set(this.constraints[e.key]) : null;
        resolved.forEach((r) => {
          const v = this.valueOf(r);
          const rule = v && v.rules && v.rules[e.sub.name];
          if (!Array.isArray(rule)) return;
          const s = new Set(rule);
          if (allowed) allowed = new Set([...allowed].filter((x) => s.has(x) || (x === 'None' && keepNone)));
          else { allowed = new Set(s); if (keepNone) allowed.add('None'); }
        });
        resolved.push(e);
        if (!allowed) return;
        const cur = e.sub.values[this.selected[e.key]];
        if (cur && allowed.has(cur.label)) return;
        // reset: the layout's default, then None, else the first allowed value
        const preferred = layoutVal.defaults && layoutVal.defaults[e.sub.name];
        let idx = preferred ? e.sub.values.findIndex((v) => v.label === preferred && allowed.has(v.label)) : -1;
        if (idx < 0) idx = e.sub.values.findIndex((v) => v.label === 'None' && allowed.has('None'));
        if (idx < 0) idx = e.sub.values.findIndex((v) => allowed.has(v.label));
        this.selected[e.key] = idx < 0 ? 0 : idx;
      });
    }

    /* ---- update (the live part) ------------------------------------------ */
    update() {
      this.enforceConstraints();
      this.resetInvalidAddons();
      this.computeUnavailable();
      const { lines, sizeLines, total, discountedTotal, resolvable, limited, preorder, latestArrival, pricedTotal, pricedDiscountedTotal, priceable } = this.resolveCart();

      // price — update the theme price node(s) or inline element
      // No purchasable combination (nothing resolves, or the current pick is
      // unavailable): show the parent product's own price instead of a total.
      // A sold-out pick whose pieces all exist still shows its real total (the Add button says Sold out).
      const soldOutPrice = !resolvable && priceable && this.selectionSoldOut();
      const shownPrice = resolvable ? total : soldOutPrice ? pricedTotal : this.graph.parentPrice;
      const shownDiscountedPrice = resolvable ? discountedTotal : soldOutPrice ? pricedDiscountedTotal : this.graph.parentPrice;
      const isDiscounted = (resolvable || soldOutPrice) && shownDiscountedPrice < shownPrice;
      if (shownPrice != null && !Number.isNaN(Number(shownPrice))) {
        let priceText = this.money.format(Number(shownPrice) / 100);
        // the parent price is a starting point, not a total: "From $X"
        if (!resolvable && !soldOutPrice && this.dataset.fromPrice) priceText = this.dataset.fromPrice.replace('__PRICE__', priceText);
        if (isDiscounted) {
          // one or more included modules are discounted: cross out the regular
          // total and show the discounted total as the sale price. No percent-off
          // badge - a single "-X%" wouldn't describe a total made of differently
          // discounted parts.
          const salePriceText = this.money.format(Number(shownDiscountedPrice) / 100);
          this.priceSaleNodes.forEach((n) => { n.textContent = salePriceText; });
          this.priceCompareNodes.forEach((n) => { n.textContent = priceText; });
          this.priceContainers.forEach((el) => el.classList.add('price--on-sale'));
        } else {
          this.priceNodes.forEach((n) => { n.textContent = priceText; });
          this.priceContainers.forEach((el) => el.classList.remove('price--on-sale'));
        }
      }
      if (this.els.addPrice) this.els.addPrice.textContent = resolvable ? this.money.format(discountedTotal / 100) : '';
      if (this.els.title) this.els.title.textContent = this.graph.productTitle || '';

      // image — real image only; hide the element when there's none (no placeholder art)
      if (this.els.image) {
        const imgSrc = this.currentImage();
        this.els.image.src = imgSrc || '';
        this.els.image.hidden = !imgSrc;
        this.els.image.alt = this.graph.productTitle || '';
      }

      // swap the theme gallery's main image(s) to the matched per-combination render
      const cfg = this.matchedConfigImage();
      this.galleryTargets.forEach((t) => {
        if (cfg) {
          t.img.setAttribute('src', cfg);
          t.img.removeAttribute('srcset');
        } else {
          if (t.src) t.img.setAttribute('src', t.src); else t.img.removeAttribute('src');
          if (t.srcset) t.img.setAttribute('srcset', t.srcset); else t.img.removeAttribute('srcset');
        }
        if (t.swiperEl && t.swiperEl.swiper) t.swiperEl.swiper.slideTo(0, 0);
      });

      // selected states + summaries
      this.paintSelections();

      // per-layout add-on icons + hide add-on blocks locked to "None"
      this.updateAddonPresentation();

      // included-modules breakdown
      this.renderModules(lines);

      // overall size + seating from the pieces' custom.dimension_size / seating_dimension
      this.renderSize(priceable ? sizeLines : []);

      // availability — unavailable options stay shown but disabled; the add button locks too
      const locked = !resolvable || limited;
      if (this.els.add) {
        this.els.add.setAttribute('aria-disabled', String(locked));
        this.els.add.disabled = locked;
      }
      if (this.els.addLabel) {
        // A bundle is only ever "Not available" or "Pre Order" (never "View in store" /
        // "Sold out"): any piece that can't be bought makes THIS configuration unavailable.
        if (this.selectionSoldOut()) this.els.addLabel.textContent = this.soldOutLabelText;
        else if (locked) this.els.addLabel.textContent = this.unavailableLabelText;
        else if (preorder) this.els.addLabel.textContent = this.dataset.preorderButtonLabel || 'Pre Order';
        else this.els.addLabel.textContent = this.addLabelText;
      }
      this.renderStockStatus(resolvable && !limited, preorder, latestArrival);
    }

    // "Pre-Order for delivery around 1st Oct" line for the CURRENT configuration. Shown only
    // when the configuration can be bought (nothing unavailable); the add button already
    // says "Not available" otherwise.
    renderStockStatus(purchasable, preorder, latestArrival) {
      const el = this.els.stockStatus;
      if (!el) return;
      let text = '';
      if (purchasable && preorder) {
        text = latestArrival
          ? this.preorderDeliveryText.replace('__DATE__', this.shortDate(latestArrival))
          : (this.dataset.preorderLabel || 'Pre Order');
      }
      el.textContent = text;
      el.hidden = !text;
    }

    todayIso() {
      const t = new Date();
      return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    }

    // Per-piece tag in the included-modules list: "Not available" or "Pre Order · 1st Oct",
    // so a shopper can see which piece drives the bundle status and swap it.
    pieceTag(variant) {
      if (variant.stock_status === 'limited' || variant.stock_status === 'viewstore') {
        return `<span class='cfg-mod__tag cfg-mod__tag--limited'>${this.esc(this.unavailableLabelText)}</span>`;
      }
      if (variant.stock_status === 'preorder') {
        const label = this.dataset.preorderLabel || 'Pre Order';
        const date = variant.arrival && variant.arrival >= this.todayIso() ? ` · ${this.shortDate(variant.arrival)}` : '';
        return `<span class='cfg-mod__tag cfg-mod__tag--preorder'>${this.esc(label + date)}</span>`;
      }
      return '';
    }

    // "2026-10-01" -> "1st Oct" (en), "1er oct." (fr), "1 okt." (others). Month names
    // follow the page language; the English ordinal suffix is only used in English.
    shortDate(iso) {
      const [y, m, d] = iso.split('-').map(Number);
      const lang = (document.documentElement.lang || 'en').toLowerCase();
      let month;
      try {
        month = new Date(y, m - 1, d).toLocaleString(lang, { month: 'short' });
      } catch (e) {
        month = new Date(y, m - 1, d).toLocaleString('en-US', { month: 'short' });
      }
      if (lang.startsWith('en')) {
        const mod = d % 10;
        const suffix = (d >= 11 && d <= 13) ? 'th' : mod === 1 ? 'st' : mod === 2 ? 'nd' : mod === 3 ? 'rd' : 'th';
        return `${d}${suffix} ${month}`;
      }
      if (lang.startsWith('fr') && d === 1) return `1er ${month}`;
      return `${d} ${month}`;
    }

    paintSelections() {
      // active + disabled buttons/swatches/tiles/selects
      this.subs.forEach((entry) => {
        const key = entry.key;
        const idx = this.selected[key];
        this.els.options.querySelectorAll(`[data-sub-key='${key}'][data-value]`).forEach((el) => {
          const vi = parseInt(el.dataset.value, 10);
          el.classList.toggle('is-selected', vi === idx);
          const dis = this.isValueDisabled(key, vi);
          el.classList.toggle('is-disabled', dis);
          el.toggleAttribute('disabled', dis);
          // sold out: the layout (see layoutBuyable) or a required add-on value out of stock in this colour
          const soldSet = entry.sub.name === 'Layout' ? this.soldOutLayouts : (this.soldOutValues || {})[key];
          const sold = !!soldSet && soldSet.has(vi);
          el.classList.toggle('is-soldout', sold);
          if (entry.sub.name === 'Layout' || soldSet) {
            el.setAttribute('data-soldout-label', sold ? this.soldOutLabelText : '');
            // announce the state to assistive technology (the ::after tag is visual only)
            const lbl = (entry.sub.values[vi] || {}).label || '';
            if (sold) el.setAttribute('aria-label', `${lbl}, ${this.soldOutLabelText}`);
            else el.removeAttribute('aria-label');
          }
        });
        const sel = this.els.options.querySelector(`select[data-sub-key='${key}']`);
        if (sel) {
          sel.value = String(idx);
          Array.from(sel.options).forEach((opt) => { opt.disabled = this.isValueDisabled(key, parseInt(opt.value, 10)); });
        }
        // per-sub current label ("Night Sky · Chenille")
        const cur = this.els.options.querySelector(`[data-sub-current='${key}']`);
        if (cur) {
          const val = entry.sub.values[idx];
          cur.textContent = val ? (val.label + (val.group_label ? ' · ' + val.group_label : '')) : '';
        }
      });

      // per-card header summary + thumbnail of the current selection
      (this.graph.options || []).forEach((opt, oi) => {
        const chosen = (opt.sub_options || []).map((sub, si) => sub.values[this.selected[`${oi}-${si}`]]);

        // Each selection is its own span; CSS inserts the "・" between them.
        // Chaise / Bumper tiles are named Left/Right/Both. The summary says which piece that is.
        const labels = chosen.map((v, i) => {
          if (!v || !v.label || v.label === 'None') return '';
          const sub = (opt.sub_options || [])[i];
          if (sub && (sub.name === 'Chaise' || sub.name === 'Bumper') && (v.label === 'Left' || v.label === 'Right' || v.label === 'Both')) {
            return v.label + ' ' + sub.name;
          }
          return v.label;
        }).filter(Boolean);
        const summaryEl = this.els.options.querySelector(`[data-card-summary='${oi}']`);
        if (summaryEl) summaryEl.innerHTML = labels.map((l) => `<span>${this.esc(l)}</span>`).join('');

        // First selected value that carries an image wins; else its colour. The
        // layout card resolves to the layout icon, the colour card to the swatch.
        const thumbEl = this.els.options.querySelector(`[data-card-thumb='${oi}']`);
        if (!thumbEl) return;
        const withThumb = chosen.find((v) => v && v.thumb);
        const withColor = chosen.find((v) => v && (v.hex || this.cssColorFromName(v.label)));
        if (withThumb) {
          thumbEl.style.backgroundColor = '';
          thumbEl.style.backgroundImage = `url("${withThumb.thumb}")`;
        } else if (withColor) {
          thumbEl.style.backgroundImage = '';
          thumbEl.style.backgroundColor = withColor.hex || this.cssColorFromName(withColor.label);
        } else {
          thumbEl.style.backgroundImage = '';
          thumbEl.style.backgroundColor = '';
        }
      });

      // left-side selection list
      if (this.els.summary) {
        this.els.summary.innerHTML = this.subs.map(({ key, sub }) => {
          const v = sub.values[this.selected[key]];
          if (!v || v.label === 'None') return '';
          return `<li><span>${this.esc(sub.name)}</span><strong>${this.esc(v.label)}</strong></li>`;
        }).join('');
      }
    }

    // The Chaise/Bumper/Ottoman add-on tiles show an icon that depends on the
    // CURRENT layout (each layout value carries an `icons` map: valueId -> URL).
    // We also hide a whole add-on block when the current LAYOUT offers no side for
    // it (layout-level gating) — but NOT when it's merely the live mutual-exclusion
    // (e.g. Bumper=Both) that leaves only "None": there the block stays visible with
    // every tile shown (conflicting ones disabled) and "None" selected.
    updateAddonPresentation() {
      const layoutEntry = this.subs.find((e) => e.sub.name === 'Layout');
      const layoutVal = layoutEntry ? (this.valueOf(layoutEntry) || {}) : {};
      const layoutRules = layoutVal.rules || {};

      this.subs.forEach((entry) => {
        const key = entry.key;

        // point each icon-tile at its value's own thumb. Tiles that had no static thumb rendered as plain
        // text, so inject an <img> if one isn't there yet. Only tile subs (Layout,
        // Chaise, Bumper, Ottoman) — never swatches/buttons.
        if (entry.sub.display_type === 'layout') {
          this.els.options.querySelectorAll(`[data-sub-key='${key}'][data-value]`).forEach((el) => {
            const val = entry.sub.values[parseInt(el.dataset.value, 10)];
            if (!val) return;
            const url = val.thumb;
            if (!url) return;
            const img = el.querySelector('img');
            if (!img) {
              el.innerHTML = `<img class='config-tile__img' src='${url}' alt='${this.esc(val.label)}'>`;
            } else if (img.getAttribute('src') !== url) {
              img.setAttribute('src', url);
            }
          });
        }

        // hide the block only when the LAYOUT itself allows no side for it; leave it
        // visible (tiles disabled, "None" selected) when a live conflict is the cause.
        if (entry.sub.hide_when_locked) {
          const layoutAllowed = layoutRules[entry.sub.name];
          const hide = Array.isArray(layoutAllowed) && layoutAllowed.filter((l) => l !== 'None').length === 0;
          const wrap = this.els.options.querySelector(`.config-sub[data-sub='${key}']`);
          if (wrap) wrap.style.display = hide ? 'none' : '';
        }
      });
    }

    /* ---- image resolution ------------------------------------------------- */
    // Fully dynamic: each graph.images entry lists the value ids it applies to.
    // An entry matches if ALL its values are currently selected; the entry that
    // matches the most values wins (most specific). Works for ANY options.
    // The config_image whose values are all currently selected (most specific wins).
    matchedConfigImage() {
      const images = Array.isArray(this.graph.images) ? this.graph.images : [];
      if (!images.length) return null;
      const selectedIds = new Set(this.subs.map((e) => (this.valueOf(e) || {}).id).filter(Boolean));
      let best = null, bestScore = -1;
      images.forEach((img) => {
        const vals = img.values || [];
        if (vals.length > bestScore && vals.every((id) => selectedIds.has(id))) {
          best = img; bestScore = vals.length;
        }
      });
      return best && best.image ? best.image : null;
    }

    formatArrival(iso) {
      const d = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(document.documentElement.lang || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }

    currentImage() {
      return this.matchedConfigImage() || this.graph.productImage || '';
    }

    /* ---- size & dimensions -------------------------------------------------
       Each piece carries custom.dimension_size ("44WX65DX28H", inches) and
       custom.seating_dimension ("24Dx16H"). Straight runs: width = sum of widths,
       depth = deepest piece. Corner layouts (3 seater + corner = 1-piece leg, 4-5 seater +
       corner = 2-piece leg, 6-7 seater U = two 2-piece legs): the leg runs along the
       depth, so depth = deepest row piece + the leg pieces' widths. Anything we can't
       place for certain (or a piece without a size) shows nothing rather than a guess. */
    parseDims(str) {
      const out = {};
      String(str || '').toUpperCase().replace(/(\d+(?:\.\d+)?)\s*([WDH])/g, (m, n, k) => { out[k] = parseFloat(n); return m; });
      return out;
    }

    formatFeet(inches) {
      if (this.sizeUnit === 'metric') {
        const cm = Math.round(inches * 2.54);
        return cm >= 100 ? `${(cm / 100).toFixed(2)} m` : `${cm} cm`;
      }
      const total = Math.round(inches);
      const ft = Math.floor(total / 12);
      const inch = total % 12;
      if (!ft) return `${inch}"`;
      return `${ft}'${inch}"`;
    }

    /* Overall size from the picked pieces' custom.dimension_size (inches).
       Shapes, read off the layout pictograms:
         - no corner                          -> one straight row (a chaise at the end only makes it deeper)
         - "Loveseat, Chaise & Corner"        -> straight row too
         - 1 corner  (or 3 corners, no chairs) -> L: back row + one leg out of the corner
         - 2 corners with end chairs / 4 corners -> U: back row + a leg at each end
         - 2 corners and no end chairs        -> straight row (the corners are the end chairs)
       The leg is the end piece plus its middles: 1 piece when the layout has 4 pieces or fewer,
       otherwise 2 (a U always has 2 per leg). The leg runs along the depth, so
       depth = deepest back-row piece + the longer leg's piece widths. Armrests add to the row width.
       Returns null if any piece has no size or the pieces don't fit a shape. */
    computeSize(lines, label = '') {
      const pieces = [];
      lines.forEach((ln) => {
        const title = String((ln.slot.module && ln.slot.module.title) || '').toLowerCase();
        if (title.includes('ottoman')) return;
        const d = this.parseDims(ln.variant.dim);
        const q = ln.slot.quantity || 1;
        for (let i = 0; i < q; i += 1) {
          pieces.push({ d, title, role: ln.slot.role || '', corner: ln.slot.kind === 'corner', armrest: title.includes('armrest') });
        }
      });
      if (!pieces.length || pieces.some((p) => !p.d.W || !p.d.D)) return null;
      const height = Math.max(...pieces.map((p) => p.d.H || 0));
      const total = (arr, key) => arr.reduce((sum, p) => sum + p.d[key], 0);
      const result = (row, legLen) => ({ width: total(row, 'W'), depth: Math.max(...row.map((p) => p.d.D)) + legLen, height });

      const armrests = pieces.filter((p) => p.armrest);
      const body = pieces.filter((p) => !p.armrest);
      const corners = body.filter((p) => p.corner);
      const others = body.filter((p) => !p.corner);
      const chairEnds = others.filter((p) => p.role || /(left|right)\s+chair/.test(p.title));
      const straightLabel = /loveseat,\s*chaise/i.test(label);
      if (!corners.length || straightLabel || body.length === 1) return result(pieces, 0);

      let bends;
      if (chairEnds.length >= 2) bends = corners.length >= 2 ? 2 : 1;
      else if (corners.length === 1) bends = 1;
      else if (corners.length === 2) return result(pieces, 0);
      else if (corners.length === 3) bends = 1;
      else if (corners.length === 4) bends = 2;
      else return null;
      const legs = bends;
      const legSize = legs === 2 ? 2 : (body.length <= 4 ? 1 : 2);
      const bendCorners = corners.slice(0, bends);
      const endPool = chairEnds.concat(corners.slice(bends));
      const mids = others.filter((p) => !chairEnds.includes(p));
      if (legs === 1 && !endPool.length) mids.sort((a, b) => Number(b.title.includes('right')) - Number(a.title.includes('right')));
      const legPieces = [];
      const legLens = [];
      let mi = 0;
      for (let g = 0; g < legs; g += 1) {
        const group = [];
        if (endPool[g]) group.push(endPool[g]);
        while (group.length < legSize && mi < mids.length) { group.push(mids[mi]); mi += 1; }
        if (group.length < legSize) return null;
        legPieces.push(...group);
        legLens.push(total(group, 'W'));
      }
      const row = bendCorners.concat(body.filter((p) => !p.corner && !legPieces.includes(p)), corners.slice(bends).filter((p) => !legPieces.includes(p)), armrests);
      return result(row, Math.max(...legLens));
    }

    renderSize(lines) {
      const el = this.els.size;
      if (!el) return;
      this.lastLines = lines;
      const wrap = this.els.sizeWrap;
      const layoutEntry = this.subs.find((e) => e.sub.name === 'Layout');
      const label = (layoutEntry && (this.valueOf(layoutEntry) || {}).label) || '';
      const size = this.computeSize(lines, label);
      if (!size) { if (wrap) wrap.hidden = true; el.innerHTML = ''; return; }
      const f = (n) => this.formatFeet(n);
      const rows = [`<li class="is-total"><span>Overall</span><strong>${f(size.width)} W × ${f(size.depth)} D${size.height ? ` × ${f(size.height)} H` : ''}</strong></li>`];
      el.innerHTML = rows.join('');
      if (wrap) {
        wrap.querySelectorAll('[data-size-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.sizeUnit === this.sizeUnit)));
        wrap.hidden = false;
      }
    }

    // "Included modules" breakdown for the current combination. Identical variants
    // are merged into one line (e.g. the roled armless ends + additive middles are
    // the same product), so a 3-seat run reads "3× Armless Chair", not three 1× rows.
    renderModules(lines) {
      if (!this.els.modules) return;
      const count = lines.reduce((n, l) => n + (l.slot.quantity || 1), 0);
      if (this.els.modulesCount) this.els.modulesCount.textContent = count ? `(${count} pieces)` : '';
      const groups = [];
      const byVariant = new Map();
      lines.forEach((ln) => {
        const q = ln.slot.quantity || 1;
        const g = byVariant.get(ln.variant.id);
        if (g) { g.q += q; return; }
        const ng = { variant: ln.variant, module: ln.slot.module, q };
        byVariant.set(ln.variant.id, ng);
        groups.push(ng);
      });
      this.els.modules.innerHTML = groups.map((g) => {
        // use the module's own discounted price when eligible, so this breakdown
        // sums to the discounted total shown above, not the regular one.
        const unitPrice = g.variant.discounted_price != null ? g.variant.discounted_price : g.variant.price;
        const lineTotal = this.money.format((unitPrice * g.q) / 100);
        const thumb = g.variant.image || g.module.featured_image; // variant image, else product default
        return `<li class='cfg-mod'>
          ${thumb ? `<img class='cfg-mod__img' src='${thumb}' alt='' width='44' height='44' loading='lazy'>` : ''}
          <span class='cfg-mod__name'><span>${g.q > 1 ? `<b>${g.q}×</b> ` : ''}${this.esc(g.module.title)}</span>${this.pieceTag(g.variant)}</span>
          <strong class='cfg-mod__price'>${lineTotal}</strong>
        </li>`;
      }).join('');
    }

    /* ---- add to cart ------------------------------------------------------ */
    addToCart() {
      const { lines, resolvable, limited, preorder, latestArrival } = this.resolveCart();
      if (!resolvable || limited || !lines.length) return;

      const parentVariantId = this.graph.parentVariantId;
      const parentSku = this.graph.parentSku;
      const parentTitle = this.graph.productTitle;
      const randomPart = Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e6).toString(36);
      const bundleId = (parentVariantId ? parentVariantId + '__' : '') + randomPart;
      const msku = parentSku ? `m${parentSku}` : '';
      const configSummary = this.subs
        .map(({ key, sub }) => sub.values[this.selected[key]])
        .filter((v) => v && v.label !== 'None')
        .map((v) => v.label)
        .join(' / ');

      // dsig (see snippets/configurator-module.liquid / snippets/collection-discount-claim.liquid)
      // is a signed "percentage:expiry:signature" claim, computed server-side (the
      // signing secret never reaches the browser) and carried as a cart line
      // property so the checkout Function (extensions/sofa-bundle-rust) can verify
      // it and apply a real price decrease to the merged bundle line.
      const byVariant = new Map();
      lines.forEach((ln) => {
        const qty = ln.slot.quantity || 1;
        const seen = byVariant.get(ln.variant.id);
        if (seen) seen.quantity += qty;
        else byVariant.set(ln.variant.id, { id: ln.variant.id, quantity: qty, dsig: ln.variant.dsig || null, preorder: ln.variant.stock_status === 'preorder', arrival: ln.variant.arrival || '' });
      });

      const bundleImage = this.currentImage();
      const items = Array.from(byVariant.values()).map((entry) => ({
        id: entry.id,
        quantity: entry.quantity,
        properties: {
          _bundle_id: bundleId,
          _bundle_config: configSummary,
          ...(bundleImage ? { _bundle_image: bundleImage } : {}),
          ...(msku ? { _msku: msku } : {}),
          ...(parentTitle ? { _bundle_title: parentTitle } : {}),
          ...(entry.dsig ? { _dsig: entry.dsig } : {}),
          // pre-order pieces are flagged like single pre-order variants
          // (snippets/next-arrival-property.liquid); the bundle-level pair marks
          // every line of a pre-order bundle with the latest arrival date.
          ...(entry.preorder ? { _pre_order_item: 'Yes' } : {}),
          ...(entry.preorder && entry.arrival ? { _backorder_arrival_date: entry.arrival } : {}),
          // visible twin on pre-order pieces only, with the piece's own date
          // (see next-arrival-property.liquid)
          ...(entry.preorder ? { 'Pre Order': entry.arrival ? `Arrives around ${this.formatArrival(entry.arrival)}` : 'Yes' } : {}),
          ...(preorder ? { _bundle_pre_order: 'Yes' } : {}),
          ...(preorder && latestArrival ? { _bundle_arrival_date: latestArrival } : {}),
        },
      }));

      this.setLoading(true);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      const body = { items };
      if (this.cart) {
        body.sections = this.cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = JSON.stringify(body);

      fetch(`${window.routes.cart_add_url}`, config)
        .then((r) => r.json())
        .then((response) => {
          if (response.status) {
            console.error('[sofa-configurator] add error', response);
            document.dispatchEvent(new CustomEvent('cart:error'));
            window.alert(response.description || response.message || (window.cartStrings && window.cartStrings.error) || 'Could not add to cart.');
            return;
          }
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sofa-configurator' });
          }
          if (this.cart) {
            this.cart.renderContents(response);
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          } else {
            window.location = window.routes.cart_url;
          }
        })
        .catch((e) => console.error('[sofa-configurator]', e))
        .finally(() => this.setLoading(false));
    }

    setLoading(on) {
      this.els.add.classList.toggle('loading', on);
      this.els.add.setAttribute('aria-disabled', String(on));
    }

    // Derive a CSS colour from a colour value's name when there's no swatch image
    // or hex — e.g. "Beige" -> beige, "Night Sky" -> nightsky (if the browser knows
    // it). Returns '' when the name isn't a valid CSS colour, so the caller can fall
    // back to a neutral grey.
    cssColorFromName(name) {
      if (!name) return '';
      const probe = new Option().style;
      const tries = [String(name).trim().toLowerCase(), String(name).replace(/\s+/g, '').toLowerCase()];
      for (const c of tries) {
        probe.color = '';
        probe.color = c;
        if (probe.color !== '') return c;
      }
      return '';
    }

    /* ---- util ------------------------------------------------------------- */
    esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
  });
}
